import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { useInfiniteList } from "@/hooks/useInfiniteList";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Mail, Search, RefreshCw, Eye, EyeOff, Users, UserPlus, Trash2, Loader2, Plus, Pencil, X, Sparkles, AlertTriangle } from "lucide-react";
import { byLastName } from "@/lib/format";
import { RuleBuilder } from "@/components/RuleBuilder";
import { useAddressCheck } from "@/lib/useAddressCheck";
import { useTableSort, SortTh, useColumns, ColumnPicker, ExportCsvButton } from "@/components/SortableHeader";

const isDynamic = (g) => !!g && (g.classification === "cloud_dynamic_distribution" || g.dynamic);

export default function DistributionLists() {
  const { can } = useAuth();
  const isAdmin = can("distributionLists.manage");
  const { t } = useLang();
  const [lists, setLists] = useState([]);
  const [q, setQ] = useState("");
  const [visFilter, setVisFilter] = useState("visible");
  const sort = useTableSort("displayName", "asc", "distributionLists");
  const cols = useColumns("distributionLists");
  const [syncing, setSyncing] = useState(false);
  const [active, setActive] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.get("/distribution-lists").then((r) => setLists(r.data));
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/groups/sync"); toast.success(`Synced ${data.synced} groups`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSyncing(false); }
  };

  const toggleVis = async (gid, isVisible) => {
    setLists((gs) => gs.map((x) => x.id === gid ? { ...x, isVisible } : x));
    try { await api.patch(`/groups/${gid}/visibility`, { isVisible }); }
    catch (e) { toast.error(apiErr(e)); setLists((gs) => gs.map((x) => x.id === gid ? { ...x, isVisible: !isVisible } : x)); }
  };

  const filtered = useMemo(() => {
    const rows = lists
      .filter((g) => `${g.displayName} ${g.mail || ""}`.toLowerCase().includes(q.toLowerCase()))
      .filter((g) => !isAdmin || visFilter === "all" || (visFilter === "visible" ? g.isVisible !== false : g.isVisible === false));
    return sort.sortRows(rows, {
      memberCount: (g) => g.memberCount ?? 0,
      isVisible: (g) => g.isVisible !== false ? 1 : 0,
    });
  }, [lists, q, isAdmin, visFilter, sort]);
  const { visible, sentinelRef, hasMore, shown, total } = useInfiniteList(filtered, 50);
  const csvHeaders = [
    { key: "displayName", label: t("dl.colName") },
    ...(cols.show("mail") ? [{ key: "mail", label: t("dl.colEmail") }] : []),
    { key: "memberCount", label: t("dl.colMembers") },
  ];
  const csvRows = filtered.map((g) => ({ displayName: g.displayName, mail: g.mail || "", memberCount: g.memberCount ?? 0 }));

  return (
    <>
      <PageHeader title={t("dl.title")} subtitle={`${filtered.length} ${t("dl.title").toLowerCase()}`}>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="dl-new-btn" className="rounded-sm bg-primary hover:bg-[#002480]">
              <Plus size={15} className="mr-2" /> {t("dl.new")}
            </Button>
            <Button onClick={sync} disabled={syncing} variant="outline" data-testid="dl-sync-now" className="rounded-sm border-zinc-300">
              <RefreshCw size={15} className={`mr-2 ${syncing ? "animate-spin" : ""}`} /> {t("groups.syncNow")}
            </Button>
          </div>
        )}
      </PageHeader>

      <div className="p-6 md:p-10">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("dl.searchPh")} data-testid="dl-search" className="rounded-sm border-zinc-300 pl-9" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ColumnPicker label={t("cols.columns")} cols={cols} columns={[{ key: "mail", label: t("dl.colEmail") }]} />
            <ExportCsvButton label={t("cols.export")} filename="distribution-lists.csv" headers={csvHeaders} rows={csvRows} />
          </div>
        </div>

        <div className="border border-zinc-200 bg-white">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                <SortTh label={t("dl.colName")} sortKey="displayName" sort={sort} />
                {cols.show("mail") && <SortTh label={t("dl.colEmail")} sortKey="mail" sort={sort} className="hidden md:table-cell" />}
                <SortTh label={t("dl.colMembers")} sortKey="memberCount" sort={sort} />
                {isAdmin && <SortTh label={t("groups.colVisible")} sortKey="isVisible" sort={sort} filter={{ value: visFilter, onChange: setVisFilter, allValue: "all", options: [
                  { value: "visible", label: t("groups.visVisible") },
                  { value: "hidden", label: t("groups.visHidden") },
                  { value: "all", label: t("groups.visAll") },
                ] }} />}
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-zinc-400">{t("dl.empty")}</td></tr>}
              {visible.map((g) => (
                <tr key={g.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`dl-row-${g.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center bg-emerald-100 text-emerald-700"><Mail size={16} /></span>
                      <div><div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">{g.displayName}{isDynamic(g) && <span data-testid={`ddg-badge-${g.id}`} className="border border-violet-200 bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700">{t("ddl.badge")}</span>}</div><div className="text-xs text-zinc-400">{g.description}</div></div>
                    </div>
                  </td>
                  {cols.show("mail") && <td className="hidden px-4 py-3 font-mono text-xs text-zinc-500 md:table-cell">{g.mail || "—"}</td>}
                  <td className="px-4 py-3 font-display text-lg font-black text-zinc-900">{g.memberCount ?? 0}</td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <button onClick={() => toggleVis(g.id, g.isVisible === false)} data-testid={`dl-vis-${g.id}`}
                        className={`flex h-8 w-8 items-center justify-center border transition-colors ${g.isVisible === false ? "border-zinc-200 bg-zinc-50 text-zinc-300 hover:text-zinc-500" : "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}>
                        {g.isVisible === false ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => setActive(g)} data-testid={`dl-manage-${g.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Users size={14} /> {t("dl.members")}</button>
                      {isAdmin && (
                        <>
                          <button onClick={() => { setEditing(g); setFormOpen(true); }} data-testid={`dl-edit-${g.id}`} title={t("dl.edit")} className="text-zinc-400 hover:text-primary"><Pencil size={15} /></button>
                          <button onClick={() => setDelTarget(g)} data-testid={`dl-delete-${g.id}`} title={t("dl.delete")} className="text-zinc-400 hover:text-red-600"><Trash2 size={15} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasMore && <div ref={sentinelRef} className="flex items-center justify-center py-4 text-xs text-zinc-400"><Loader2 size={14} className="mr-2 animate-spin" /> {shown}/{total}</div>}
        </div>
      </div>

      {active && (isDynamic(active)
        ? <DDGMembersDialog list={active} onClose={() => setActive(null)} />
        : <MembersDialog list={active} onClose={() => { setActive(null); load(); }} />)}
      {formOpen && <DLFormDialog list={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
      {delTarget && <DeleteDialog list={delTarget} dynamic={isDynamic(delTarget)} onClose={() => setDelTarget(null)} onDeleted={() => { setDelTarget(null); load(); }} />}
    </>
  );
}

function DomainSelect({ value, onChange, testid, options, defaultDomainId, t }) {
  if (!options.length) {
    return <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="domain.com" data-testid={testid} className="w-44 shrink-0 rounded-sm border-zinc-300 font-mono text-sm" />;
  }
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger title={value} className="w-72 shrink-0 rounded-sm border-zinc-300 font-mono text-xs" data-testid={testid}><SelectValue placeholder="@domain" /></SelectTrigger>
      <SelectContent>{options.map((d) => <SelectItem key={d} value={d}>@{d}{d === defaultDomainId ? ` ${t("dl.tenantDefault")}` : ""}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function DLFormDialog({ list, onClose, onSaved }) {
  const { t } = useLang();
  const editing = !!list;
  const split = (a) => { const i = (a || "").indexOf("@"); return i < 0 ? { local: a || "", domain: "" } : { local: a.slice(0, i), domain: a.slice(i + 1) }; };
  const initPrimary = split(list?.mail || "");
  const [type, setType] = useState(isDynamic(list) ? "dynamic" : "static");
  const [name, setName] = useState(list?.displayName || "");
  const [primaryLocal, setPrimaryLocal] = useState(initPrimary.local);
  const [primaryDomain, setPrimaryDomain] = useState(initPrimary.domain);
  const [aliasRows, setAliasRows] = useState((list?.aliases || []).map(split));
  const [allowExternal, setAllowExternal] = useState(list?.allowExternal || false);
  const [hiddenFromGal, setHiddenGal] = useState(!!list?.hiddenFromGal);
  const [rule, setRuleState] = useState({ clauses: list?.filterClauses || [], join: list?.filterJoin || "-and" });
  const [domains, setDomains] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/domains").then((r) => {
      const doms = r.data || [];
      setDomains(doms);
      const def = (doms.find((d) => d.isDefault) || doms[0] || {}).id || "";
      if (def) setPrimaryDomain((p) => p || def);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const defaultDomainId = (domains.find((d) => d.isDefault) || {}).id || "";
  const domainOptions = useMemo(() => {
    const ids = [];
    domains.forEach((d) => ids.push(d.id));
    [primaryDomain, ...aliasRows.map((a) => a.domain)].forEach((d) => { if (d && !ids.includes(d)) ids.push(d); });
    return ids;
  }, [domains, primaryDomain, aliasRows]);

  const setAlias = (i, patch) => setAliasRows((rows) => rows.map((r, x) => (x === i ? { ...r, ...patch } : r)));
  const addAlias = () => setAliasRows((rows) => [...rows, { local: "", domain: defaultDomainId || domainOptions[0] || "" }]);
  const removeAlias = (i) => setAliasRows((rows) => rows.filter((_, x) => x !== i));
  const onRule = (_r, extra) => { if (extra) setRuleState(extra); };
  const isDyn = type === "dynamic";

  const composed = (loc, dom) => (loc && loc.trim() && dom ? `${loc.trim()}@${dom}` : "");
  const primaryAddr = composed(primaryLocal, primaryDomain);
  const aliasAddrs = aliasRows.map((a) => composed(a.local, a.domain));
  const { conflicts } = useAddressCheck([primaryAddr, ...aliasAddrs], editing ? list?.id : null);
  const conflictFor = (addr) => (addr && conflicts[addr]) ? conflicts[addr] : null;
  const ConflictHint = ({ addr }) => {
    const c = conflictFor(addr);
    if (!c) return null;
    return <p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-700" data-testid={`addr-conflict-${addr}`}><AlertTriangle size={12} /> {t("addr.inUse")} {t(`addr.type.${c.type}`)} "{c.displayName}"</p>;
  };

  const save = async () => {
    if (!name.trim() || !primaryLocal.trim() || !primaryDomain) { toast.error(t("dl.required")); return; }
    const email = primaryAddr;
    const aliases = aliasRows.filter((r) => r.local.trim() && r.domain).map((r) => `${r.local.trim()}@${r.domain}`);
    if (isDyn && !rule.clauses.length) { toast.error(t("convert.needRule")); return; }
    if (Object.keys(conflicts).length) { toast.error(t("addr.conflictSave")); return; }
    setBusy(true);
    try {
      if (isDyn) {
        const body = { name: name.trim(), email, aliases, allowExternal, hiddenFromGal, join: rule.join, clauses: rule.clauses };
        if (editing) { await api.patch(`/dynamic-distribution-lists/${list.id}`, body); toast.success(t("ddl.updated")); }
        else { await api.post("/dynamic-distribution-lists", body); toast.success(t("ddl.created")); }
      } else {
        const body = { name: name.trim(), email, aliases, allowExternal, hiddenFromGal };
        if (editing) { await api.put(`/distribution-lists/${list.id}`, body); toast.success(t("dl.updated")); }
        else { await api.post("/distribution-lists", body); toast.success(t("dl.created")); }
      }
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-lg" data-testid="dl-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">{isDyn ? <Sparkles size={16} /> : <Mail size={16} />} {editing ? t("dl.edit") : t("dl.new")}</DialogTitle>
          <DialogDescription>{t("dl.formDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!editing && (
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("dl.typeLabel")}</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="dl-form-type" className="rounded-sm border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="static">{t("dl.typeStatic")}</SelectItem>
                  <SelectItem value="dynamic">{t("dl.typeDynamic")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("dl.fieldName")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("dl.namePh")} data-testid="dl-form-name" className="rounded-sm border-zinc-300" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("dl.fieldEmail")}</Label>
            <div className="flex items-center gap-2">
              <Input value={primaryLocal} onChange={(e) => setPrimaryLocal(e.target.value)} placeholder={t("dl.localPh")} data-testid="dl-form-email-local" disabled={editing && isDyn} className="rounded-sm border-zinc-300 font-mono text-sm disabled:bg-zinc-100" />
              <span className="text-sm font-semibold text-zinc-400">@</span>
              <DomainSelect value={primaryDomain} onChange={setPrimaryDomain} testid="dl-form-email-domain" options={domainOptions} defaultDomainId={defaultDomainId} t={t} />
            </div>
            <ConflictHint addr={primaryAddr} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("dl.fieldAliases")}</Label>
            <div className="space-y-2">
              {aliasRows.length === 0 && <p className="text-xs text-zinc-400">{t("dl.noAliases")}</p>}
              {aliasRows.map((a, i) => (
                <div key={i}>
                  <div className="flex items-center gap-2">
                    <Input value={a.local} onChange={(e) => setAlias(i, { local: e.target.value })} placeholder={t("dl.localPh")} data-testid={`dl-form-alias-local-${i}`} className="rounded-sm border-zinc-300 font-mono text-sm" />
                    <span className="text-sm font-semibold text-zinc-400">@</span>
                    <DomainSelect value={a.domain} onChange={(v) => setAlias(i, { domain: v })} testid={`dl-form-alias-domain-${i}`} options={domainOptions} defaultDomainId={defaultDomainId} t={t} />
                    <button onClick={() => removeAlias(i)} data-testid={`dl-form-alias-remove-${i}`} className="p-1.5 text-zinc-400 hover:text-red-600"><X size={15} /></button>
                  </div>
                  <ConflictHint addr={aliasAddrs[i]} />
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addAlias} data-testid="dl-form-alias-add" className="rounded-sm border-zinc-300"><Plus size={14} className="mr-1.5" /> {t("dl.addAlias")}</Button>
            </div>
            <p className="text-xs text-zinc-400">{t("dl.aliasesHelp")}</p>
          </div>
          {isDyn && (
            <div className="border border-violet-200 bg-white p-3">
              <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-violet-700"><Sparkles size={13} /> {t("ddl.desc")}</div>
              <RuleBuilder initialClauses={list?.filterClauses} initialJoin={list?.filterJoin} onChange={onRule} testid="dl-rule" variant="exo" preview templates />
            </div>
          )}
          <div className="flex items-center justify-between border border-zinc-200 px-3 py-2.5">
            <div className="pr-3">
              <div className="text-sm font-medium text-zinc-800">{t("dl.allowExternal")}</div>
              <div className="text-xs text-zinc-400">{t("dl.allowExternalHelp")}</div>
            </div>
            <Switch checked={allowExternal} onCheckedChange={setAllowExternal} data-testid="dl-form-external" />
          </div>
          <div className="flex items-center justify-between border border-zinc-200 px-3 py-2.5">
            <div className="pr-3">
              <div className="text-sm font-medium text-zinc-800">{t("gal.hide")}</div>
              <div className="text-xs text-zinc-400">{t("gal.hideHelp")}</div>
            </div>
            <Switch checked={hiddenFromGal} onCheckedChange={setHiddenGal} data-testid="dl-form-hidegal" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="dl-form-cancel" className="rounded-sm border-zinc-300">{t("dl.cancel")}</Button>
          <Button onClick={save} disabled={busy} data-testid="dl-form-save" className="rounded-sm bg-primary hover:bg-[#002480]">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{editing ? t("dl.save") : t("dl.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ list, dynamic, onClose, onDeleted }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    const url = dynamic ? `/dynamic-distribution-lists/${list.id}` : `/distribution-lists/${list.id}`;
    try { await api.delete(url); toast.success(t("dl.deleted")); onDeleted(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none sm:max-w-md" data-testid="dl-delete-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{t("dl.deleteConfirm")}</DialogTitle>
          <DialogDescription>{t("dl.deleteDesc")} <span className="font-semibold text-zinc-700">{list.displayName}</span> ({list.mail})</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="dl-delete-cancel" className="rounded-sm border-zinc-300">{t("dl.cancel")}</Button>
          <Button onClick={del} disabled={busy} data-testid="dl-delete-confirm" className="rounded-sm bg-red-600 hover:bg-red-700">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{t("dl.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({ list, onClose }) {
  const { t } = useLang();
  const [members, setMembers] = useState([]);
  const [live, setLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/distribution-lists/${list.id}/members`)
      .then((r) => { setMembers(r.data.members || []); setLive(r.data.live); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [list.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const memberSmtps = useMemo(() => new Set(members.map((m) => (m.PrimarySmtpAddress || m.primarySmtpAddress || "").toLowerCase())), [members]);
  const candidates = useMemo(() => users
    .filter((u) => u.mail || u.userPrincipalName)
    .filter((u) => !memberSmtps.has((u.mail || u.userPrincipalName || "").toLowerCase()))
    .filter((u) => `${u.displayName || ""} ${u.mail || ""} ${u.userPrincipalName || ""}`.toLowerCase().includes(q.toLowerCase()))
    .sort(byLastName)
    .slice(0, 50), [users, memberSmtps, q]);

  const addUser = async (u) => {
    const id = u.mail || u.userPrincipalName;
    setBusy(true);
    try { await api.post(`/distribution-lists/${list.id}/members`, { id }); toast.success(t("dl.addQueued")); setQ(""); setPickerOpen(false); setTimeout(load, 1500); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const remove = async (smtp) => {
    setAdding(smtp);
    try { await api.delete(`/distribution-lists/${list.id}/members/${encodeURIComponent(smtp)}`); toast.success(t("dl.removeQueued")); setTimeout(load, 1500); }
    catch (e) { toast.error(apiErr(e)); } finally { setAdding(""); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none sm:max-w-lg" data-testid="dl-members-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Mail size={16} /> {list.displayName}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{list.mail}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={q} onChange={(e) => { setQ(e.target.value); setPickerOpen(true); }} onFocus={() => setPickerOpen(true)} placeholder={t("dl.searchEmployee")} data-testid="dl-add-member-search" disabled={busy} className="rounded-sm border-zinc-300 pl-9 text-sm" />
              {busy && <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-zinc-400" />}
            </div>
            {pickerOpen && q && (
              <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto border border-zinc-200 bg-white shadow-lg" data-testid="dl-add-candidates">
                {candidates.length === 0 && <div className="px-3 py-3 text-sm text-zinc-400">{t("dl.noEmployee")}</div>}
                {candidates.map((u) => (
                  <button key={u.id} type="button" onClick={() => addUser(u)} disabled={busy} data-testid={`dl-add-candidate-${u.id}`} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-zinc-50 disabled:opacity-50">
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-zinc-800">{u.displayName}</span>
                      <span className="block truncate font-mono text-xs text-zinc-400">{u.mail || u.userPrincipalName}</span>
                    </span>
                    <UserPlus size={15} className="shrink-0 text-primary" />
                  </button>
                ))}
              </div>
            )}
          </div>
          {!live && <p className="text-xs text-amber-600">{t("dl.notLive")}</p>}
          <div className="max-h-80 overflow-y-auto border border-zinc-200">
            {loading && <div className="px-4 py-8 text-center text-xs text-zinc-400"><Loader2 size={16} className="mx-auto animate-spin" /></div>}
            {!loading && members.length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-400">{t("dl.noMembers")}</div>}
            {members.map((m, i) => {
              const smtp = m.PrimarySmtpAddress || m.primarySmtpAddress || "";
              return (
                <div key={smtp + i} className="flex items-center justify-between border-b border-zinc-100 px-3 py-2 last:border-0" data-testid={`dl-member-${i}`}>
                  <div className="min-w-0"><div className="truncate text-sm text-zinc-800">{m.DisplayName || m.displayName || smtp}</div><div className="truncate font-mono text-xs text-zinc-400">{smtp}</div></div>
                  <button onClick={() => remove(smtp)} disabled={adding === smtp} data-testid={`dl-remove-${i}`} className="ml-2 text-zinc-300 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function DDGMembersDialog({ list, onClose }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/dynamic-distribution-lists/${list.id}/preview-members`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e))); }, [list.id]);
  const members = data?.members || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-lg" data-testid="ddg-members-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Sparkles size={16} /> {list.displayName}</DialogTitle>
          <DialogDescription>{t("ddl.desc")}</DialogDescription>
        </DialogHeader>
        <div className="border border-zinc-200 bg-white px-3 py-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t("rule.preview")}</div>
          <code className="block break-all font-mono text-[11px] text-zinc-800">{list.recipientFilter}</code>
        </div>
        <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ddl.previewMembers")} ({members.length})</div>
        <div className="max-h-80 overflow-y-auto border border-zinc-200">
          {!data && <div className="px-4 py-8 text-center text-xs text-zinc-400"><Loader2 size={16} className="mx-auto animate-spin" /></div>}
          {data && members.length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-400">{t("ddl.noPreview")}</div>}
          {members.map((m, i) => (
            <div key={m.id + i} className="border-b border-zinc-100 px-3 py-2 last:border-0" data-testid={`ddg-member-${i}`}>
              <div className="text-sm text-zinc-800">{m.displayName}</div>
              <div className="font-mono text-xs text-zinc-400">{m.userPrincipalName || m.mail}</div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
